import { useMemo, useState, type ReactNode } from 'react';
import { useNavigate } from '@tanstack/react-router';

import { CheckCheckIcon, InboxIcon, SettingsIcon } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';

import { badgeText } from '../badgeText';
import { INBOX_COPY, NOTIFICATIONS_PATH, SETTINGS_PATH } from '../copy';
import { splitByDay } from '../dayGroups';
import { EMPTY_VIEW, type InboxView } from '../emptyViews';
import { groupByTask, type InboxRow as Row } from '../groupByTask';
import { BELL_LIMIT, useInbox } from '../hooks/useInbox';
import { useMarkRead } from '../hooks/useMarkRead';
import { useOpenNotification } from '../hooks/useOpenNotification';
import type { InboxFilter } from '../queryKeys';
import { InboxRow } from './InboxRow';

/** Archived lives on the notifications page now (spec 147), not in the bell. */
type View = Exclude<InboxView, 'archived'>;

/** The tabs (spec 141 US4). */
const TABS: { id: View; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'needs_you', label: 'Needs you' },
  { id: 'unread', label: 'Unread' },
];

const EMPTY = EMPTY_VIEW;

/** The filter's "no filter" entry; never a workspace id, which is a UUID. */
const ALL_WORKSPACES = 'all';

/**
 * The inbox (spec 131 US2): every workspace, grouped by task, newest first.
 * The newest 30 only (spec 147): "See all notifications" opens the page that
 * holds every one, on the tab the bell was on.
 * Opening a row marks its task's notifications read and goes to the task on
 * its board. Laid out to one 16px left edge (spec 141): header, tabs, rows
 * and footer all start there.
 */
export function InboxPanel({
  unread,
  onNavigate,
}: {
  unread: number | undefined;
  /** Called once a row has been opened, so the popover can close. */
  onNavigate: () => void;
}) {
  const [view, setView] = useState<View>('all');
  const [workspaceId, setWorkspaceId] = useState<string | undefined>(undefined);
  const filter: InboxFilter = {
    unread: view === 'unread' ? true : undefined,
    group: view === 'needs_you' ? 'needs_you' : undefined,
    workspaceId,
  };
  const inbox = useInbox(filter, true, BELL_LIMIT);
  // The Needs-you tab's count: its unread rows only (spec 141 FR-003), so a
  // request of its own. The tab itself lists read rows too.
  const waiting = useInbox({ group: 'needs_you', unread: true, workspaceId });
  const waitingCount = waiting.notifications?.length ?? 0;
  const mark = useMarkRead();
  const navigate = useNavigate();
  const openNotification = useOpenNotification();
  const rows = useMemo(
    () => groupByTask(inbox.notifications ?? []),
    [inbox.notifications],
  );
  const days = useMemo(
    () => splitByDay(rows, (row) => row.latest.created_at),
    [rows],
  );
  // The workspaces this person has notifications in, for the filter. Kept
  // across views: once one workspace is picked the list holds only its rows,
  // and the others must stay on offer.
  const [workspaces, setWorkspaces] = useState<[string, string][]>([]);
  const unseen = (inbox.notifications ?? []).filter(
    (n) => !workspaces.some(([id]) => id === n.workspace.id),
  );
  if (unseen.length > 0) {
    const seen = new Map(workspaces);
    for (const n of unseen) {
      seen.set(n.workspace.id, n.workspace.name ?? 'Workspace');
    }
    setWorkspaces([...seen.entries()]);
  }

  function open(row: Row) {
    onNavigate();
    openNotification(row.latest);
  }

  function renderRows(list: Row[]) {
    return list.map((row) => (
      <InboxRow
        key={row.key}
        row={row}
        onOpen={open}
        onArchive={(r) => {
          for (const item of r.items) {
            mark.mutate({ kind: 'archive', notificationId: item.id });
          }
        }}
        onMarkUnread={(r) => {
          mark.mutate({ kind: 'unread', notificationId: r.latest.id });
        }}
        onAcknowledge={(r) => {
          mark.mutate({ kind: 'acknowledge', notificationId: r.latest.id });
        }}
      />
    ));
  }

  const empty = view === 'all' && workspaceId ? null : EMPTY[view];

  return (
    <div className="flex w-[22rem] max-w-[calc(100vw-2rem)] flex-col">
      <div className="flex items-center gap-2 ps-4 pe-2 pt-2.5">
        <h2 className="text-foreground text-sm leading-7 font-medium">
          Notifications
        </h2>
        {unread ? (
          <span
            aria-hidden="true"
            className="bg-primary text-primary-foreground min-w-[18px] rounded-full px-1.5 text-center text-2xs leading-[18px] font-medium tabular-nums"
          >
            {badgeText(unread)}
          </span>
        ) : null}
        <div className="ms-auto flex items-center gap-0.5">
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label="Mark all read"
            title="Mark all read"
            disabled={!unread}
            onClick={() => {
              mark.mutate({ kind: 'all' });
            }}
          >
            <CheckCheckIcon aria-hidden="true" strokeWidth={1.5} />
          </Button>
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label="Notification settings"
            title="Notification settings"
            onClick={() => {
              onNavigate();
              void navigate({ to: SETTINGS_PATH });
            }}
          >
            <SettingsIcon aria-hidden="true" strokeWidth={1.5} />
          </Button>
        </div>
      </div>
      <div className="border-border mt-1 flex items-end gap-4 border-b px-4">
        <div role="group" aria-label="Show" className="flex gap-4">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              type="button"
              aria-pressed={view === tab.id}
              aria-label={
                tab.id === 'needs_you' && waitingCount > 0
                  ? `${tab.label}, ${countText(waitingCount, waiting.hasMore)} unread`
                  : undefined
              }
              onClick={() => {
                setView(tab.id);
              }}
              className={cn(
                'focus-visible:ring-ring -mb-px inline-flex items-center gap-1.5 border-b-2 pt-2 pb-[7px] text-xs leading-5 transition-colors focus-visible:ring-3 focus-visible:outline-none pointer-coarse:min-h-11',
                view === tab.id
                  ? 'border-primary text-foreground font-medium'
                  : 'text-muted-foreground hover:text-foreground border-transparent',
              )}
            >
              {tab.label}
              {tab.id === 'needs_you' && waitingCount > 0 ? (
                <span
                  aria-hidden="true"
                  className="text-muted-foreground text-2xs tabular-nums"
                >
                  {countText(waitingCount, waiting.hasMore)}
                </span>
              ) : null}
            </button>
          ))}
        </div>
        {workspaces.length > 1 || workspaceId ? (
          // The app's own select, not a native one: the browser's list did not
          // look like any other menu here. Borderless, so it sits in the tab
          // bar as a filter rather than a form field.
          <Select
            value={workspaceId ?? ALL_WORKSPACES}
            onValueChange={(next) => {
              setWorkspaceId(
                next && next !== ALL_WORKSPACES ? next : undefined,
              );
            }}
          >
            <SelectTrigger
              aria-label="Workspace"
              className="text-muted-foreground hover:text-foreground ms-auto mb-1.5 h-6 w-auto pointer-coarse:mb-0 max-w-32 min-w-0 gap-1 border-0 bg-transparent px-1.5 text-xs [&_svg]:size-3.5"
            >
              <SelectValue>
                {(id: string | undefined) => (
                  <span className="truncate">
                    {id && id !== ALL_WORKSPACES
                      ? (workspaces.find(([key]) => key === id)?.[1] ??
                        'Workspace')
                      : 'All workspaces'}
                  </span>
                )}
              </SelectValue>
            </SelectTrigger>
            <SelectContent className="max-h-72 overflow-y-auto">
              <SelectItem value={ALL_WORKSPACES}>All workspaces</SelectItem>
              {workspaces.map(([id, name]) => (
                <SelectItem key={id} value={id}>
                  {name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : null}
      </div>
      <div className="max-h-[26rem] overflow-y-auto px-1.5 pb-1.5">
        {inbox.isPending ? (
          <Note>Loading…</Note>
        ) : inbox.loadFailed ? (
          <div className="flex flex-col items-center gap-2 px-4 py-6 text-center">
            <p className="text-muted-foreground text-sm">
              Notifications could not be loaded.
            </p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => void inbox.refetch()}
            >
              Try again
            </Button>
          </div>
        ) : rows.length === 0 ? (
          <div className="flex flex-col items-center gap-2 px-6 py-8 text-center">
            {empty ? (
              <empty.icon
                aria-hidden="true"
                className="text-muted-foreground size-6"
                strokeWidth={1.5}
              />
            ) : (
              <InboxIcon
                aria-hidden="true"
                className="text-muted-foreground size-6"
                strokeWidth={1.5}
              />
            )}
            <p className="text-muted-foreground text-sm">
              {empty ? empty.line : 'Nothing here.'}
            </p>
          </div>
        ) : (
          <ul aria-label="Notifications" className="flex flex-col">
            {days.today.length > 0 ? (
              <DayHeading>{INBOX_COPY.today}</DayHeading>
            ) : null}
            {renderRows(days.today)}
            {days.earlier.length > 0 ? (
              <DayHeading>{INBOX_COPY.earlier}</DayHeading>
            ) : null}
            {renderRows(days.earlier)}
          </ul>
        )}
      </div>
      <div className="border-border flex items-center border-t ps-4 pe-2 py-1.5 pointer-coarse:py-0">
        <button
          type="button"
          onClick={() => {
            onNavigate();
            void navigate({
              to: NOTIFICATIONS_PATH,
              search: view === 'all' ? {} : { status: view },
            });
          }}
          // The footer's whole width on touch, 44px tall (spec 274).
          className="text-primary focus-visible:ring-ring rounded-sm text-xs leading-6 font-medium hover:underline focus-visible:ring-3 focus-visible:outline-none pointer-coarse:min-h-11 pointer-coarse:flex-1 pointer-coarse:text-start"
        >
          {INBOX_COPY.seeAll}
        </button>
      </div>
    </div>
  );
}

/** "50+" when there is another page of them. */
function countText(count: number, more: boolean): string {
  return more ? `${String(count)}+` : String(count);
}

export function DayHeading({ children }: { children: ReactNode }) {
  // A list item, so the list stays a list; not a row, so nothing announces
  // it as a notification.
  return (
    <li
      role="presentation"
      className="text-muted-foreground px-2.5 pt-3 pb-1 text-2xs font-medium tracking-wider uppercase"
    >
      {children}
    </li>
  );
}

function Note({ children }: { children: ReactNode }) {
  return (
    <p className="text-muted-foreground px-4 py-6 text-center text-sm">
      {children}
    </p>
  );
}
